import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Lane, LanesResponse } from '../../interfaces/services/lanes';
import { NetworkKpi } from '../../interfaces/services/kpis';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';

interface LaneState {
    laneApiStatus: ApiStatus;
    lanes: Lane[];
    laneCount: number;
    kpiApiStatus: ApiStatus;
    kpis: NetworkKpi;
}

/** Thunks */
export const fetchLanes = createAsyncThunk(
    'lanes/fetchLanes',
    async ({
        filter_criteria,
    }: {
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes`,
                body: {
                    pickup_service_area_id: null,
                    delivery_service_area_id: null,
                    filter_criteria,
                },
            }) as AxiosResponse<LanesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load lanes', error);
            toast.error('Unable to load lanes');
            throw error;
        }
    }
);

export const fetchLaneKpis = createAsyncThunk(
    'lanes/fetchLaneKpis',
    async ({
        filter_criteria,
    }: {
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/kpi`,
                body: {
                    filter_criteria,
                },
            }) as AxiosResponse<NetworkKpi>;

            return data;
        } catch (error) {
            console.error('Unable to load lane KPIs', error);
            toast.error('Unable to load lane KPIs');
            throw error;
        }
    }
);

const initialState: LaneState = {
    laneApiStatus: ApiStatus.Idle,
    lanes: [],
    laneCount: 0,
    kpiApiStatus: ApiStatus.Idle,
    kpis: {
        carrier_network_grade: null,
        lane_count: null,
        freight_order_count: null,
        total_revenue_usd_amount: null,
        linehaul_revenue_usd_amount: null,
        fuel_surcharge_usd_amount: null,
        accessorial_revenue_usd_amount: null,
        travel_distance_miles_amount: null,
        billable_distance_miles_amount: null,
        loaded_distance_miles_amount: null,
        empty_distance_miles_amount: null,
        pta_to_last_stop_departure_hours_duration: null,
        dispatch_to_last_stop_departure_hours_duration: null,
        first_stop_to_last_stop_hours_duration: null,
        first_stop_departure_to_final_stop_arrival_hours_duration: null,
        standard_transit_hours_duration: null,
        netwise_dwell_hours_duration: null,
        netwise_in_transit_delay_hours_duration: null,
        netwise_delay_hours_duration: null,
        load_hours_duration: null,
        unload_hours_duration: null,
        empty_transit_hours_duration: null,
        pickup_party_count: null,
        commodity_count: null,
        average_dispatch_to_last_stop_departure_hours_duration: null,
        average_first_stop_to_last_stop_hours_duration: null,
        average_first_stop_departure_to_final_stop_arrival_hours_duration: null,
        average_standard_transit_hours_duration: null,
        average_netwise_in_transit_delay_hours_duration: null,
        average_load_hours_duration: null,
        average_unload_hours_duration: null,
        average_empty_transit_hours_duration: null,
        average_network_order_yield_usd: null,
    },
};

export const lanesSlice = createSlice({
    name: 'lanes',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchLanes.pending, (state) => {
            state.laneApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchLanes.fulfilled, (state, action) => {
            state.laneApiStatus = ApiStatus.Success;
            state.lanes = action.payload.lanes;
            state.laneCount = action.payload.lane_count;
        });
        builder.addCase(fetchLanes.rejected, (state) => {
            state.laneApiStatus = ApiStatus.Failure;
            state.lanes = [];
            state.laneCount = 0;
        });
        builder.addCase(fetchLaneKpis.pending, (state) => {
            state.kpiApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchLaneKpis.fulfilled, (state, action) => {
            state.kpiApiStatus = ApiStatus.Success;
            state.kpis = action.payload;
        });
        builder.addCase(fetchLaneKpis.rejected, (state) => {
            state.kpiApiStatus = ApiStatus.Failure;
            state.kpis = initialState.kpis;
        });
    },
});

export default lanesSlice.reducer;
